import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import SmartCardIcon from "@mui/icons-material/Contactless";
import ShareIcon from "@mui/icons-material/Share";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { toast } from "react-toastify";
import { SubmitCardPurchaseRequest } from "../CardPurchaseData.ts";

const CardPurchaseDialog = ({ open, onClose, userEmail }) => {
  const [email, setEmail] = useState(userEmail || "");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setEmail(userEmail || "");
  }, [userEmail]);

  const handleSubmit = async () => {
    if (!email || !phone) {
      toast.error("Please fill in all fields.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await SubmitCardPurchaseRequest({ email, phone });

      toast.success(
        "Request submitted successfully! Our agents will contact you soon.",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
      setSubmitted(true);
    } catch (error) {
      toast.error(
        error.message || "Failed to submit request. Please try again.",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setEmail(userEmail || "");
    setPhone("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="div">
            Get Your NFC Card
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {!submitted ? (
          <>
            {/* Promotion Section */}
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Typography
                variant="h5"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Unlock Instant Profile Sharing!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Tap your NFC card on any phone to instantly open your public
                profile.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box
                  component="img"
                  src="/assets/images/covers/CustomNFCCard.webp"
                  alt="NFC Card Bundle"
                  sx={{
                    width: "100%",
                    maxWidth: 250,
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Box>

              <List sx={{ bgcolor: "background.paper", borderRadius: 1, p: 1 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SmartCardIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Professional and sleek design"
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneAndroidIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Instant profile access on any device"
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <BusinessCenterIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Boost your networking opportunities"
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ShareIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Easy to carry and share everywhere"
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            {/* Form Section */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Phone Number"
                type="number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                margin="normal"
                required
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your request has been submitted. Our agents will contact you soon
              for more details.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {!submitted ? (
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={isSubmitting}
            fullWidth
          >
            Submit Request
          </LoadingButton>
        ) : (
          <Button onClick={handleClose} variant="contained" fullWidth>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CardPurchaseDialog;
